/**
 * PresenceService - Online presence tracking for users
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { authService } from '../auth';
import { friendsService } from './FriendsService';
import type { PresenceStatus, UserPresence, PresenceChangeCallback, SocialError } from './types';
import type { RealtimeChannel } from '@supabase/supabase-js';

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 1 minute
const ACTIVITY_DEBOUNCE_MS = 30 * 1000; // 30 seconds - don't make DB calls more often than this

export class PresenceService {
  private currentStatus: PresenceStatus = 'offline';
  private currentRoomId: string | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private idleTimeout: ReturnType<typeof setTimeout> | null = null;
  private _lastActivityTime: number = Date.now();
  private _lastActivityDbCall: number = 0; // Track when we last made a DB call for activity
  private presenceChannel: RealtimeChannel | null = null;
  private friendsPresence: Map<string, UserPresence> = new Map();
  private listeners: Set<PresenceChangeCallback> = new Set();
  private isInitialized = false;

  /**
   * Initialize presence tracking
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    const user = authService.getCurrentUser();
    if (!user || !isSupabaseConfigured() || !supabase) return;

    this.isInitialized = true;
    
    // Set initial online status
    await this.setOnline();

    // Start heartbeat
    this.startHeartbeat();

    // Set up activity listeners
    this.setupActivityListeners();

    // Subscribe to friends' presence
    await this.subscribeToFriendsPresence();
  }

  /**
   * Cleanup presence tracking
   */
  async cleanup(): Promise<void> {
    if (!this.isInitialized) return;

    await this.setOffline();
    this.stopHeartbeat();
    this.removeActivityListeners();
    
    if (this.presenceChannel) {
      await supabase?.removeChannel(this.presenceChannel);
      this.presenceChannel = null;
    }

    this.isInitialized = false;
  }

  /**
   * Check if Supabase is available
   */
  private checkSupabaseAvailable(): SocialError | null {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        message: 'Presence features are not configured.',
        code: 'SUPABASE_NOT_CONFIGURED',
      };
    }
    return null;
  }

  /**
   * Get current user ID
   */
  private getCurrentUserId(): string | null {
    return authService.getCurrentUser()?.id || null;
  }

  /**
   * Update presence in database
   */
  async updatePresence(status: PresenceStatus, roomId?: string | null): Promise<{ error: SocialError | null }> {
    const configError = this.checkSupabaseAvailable();
    if (configError) return { error: configError };

    const userId = this.getCurrentUserId();
    if (!userId) {
      return { error: { message: 'Not authenticated', code: 'NOT_AUTHENTICATED' } };
    }

    try {
      this.currentStatus = status;
      this.currentRoomId = roomId ?? this.currentRoomId;

      const { error } = await supabase!
        .from('presence')
        .upsert({
          user_id: userId,
          status,
          current_room_id: this.currentRoomId,
          last_seen: new Date().toISOString(),
        });

      if (error) {
        return { error: { message: error.message, code: error.code } };
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Failed to update presence' } };
    }
  }

  /**
   * Set status to online
   */
  async setOnline(): Promise<{ error: SocialError | null }> {
    this._lastActivityTime = Date.now();
    this.resetIdleTimer();
    return this.updatePresence('online');
  }

  /**
   * Set status to in_game with room ID
   */
  async setInGame(roomId: string): Promise<{ error: SocialError | null }> {
    this._lastActivityTime = Date.now();
    this.resetIdleTimer();
    return this.updatePresence('in_game', roomId);
  }

  /**
   * Set status to away
   */
  async setAway(): Promise<{ error: SocialError | null }> {
    return this.updatePresence('away');
  }

  /**
   * Set status to offline
   */
  async setOffline(): Promise<{ error: SocialError | null }> {
    return this.updatePresence('offline', null);
  }

  /**
   * Get current presence status
   */
  getCurrentStatus(): PresenceStatus {
    return this.currentStatus;
  }

  /**
   * Get current room ID
   */
  getCurrentRoomId(): string | null {
    return this.currentRoomId;
  }

  /**
   * Subscribe to friends' presence changes
   */
  async subscribeToFriendsPresence(): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) return;

    const userId = this.getCurrentUserId();
    if (!userId) return;

    // Get friend IDs
    const { data: friends } = await friendsService.getFriends();
    if (!friends || friends.length === 0) return;

    const friendIds = friends.map((f) => f.id);

    // Load initial presence
    await this.loadFriendsPresence(friendIds);

    // Subscribe to realtime updates
    this.presenceChannel = supabase
      .channel('friends-presence')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'presence',
          filter: `user_id=in.(${friendIds.join(',')})`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const presence = payload.new as {
              user_id: string;
              status: PresenceStatus;
              current_room_id: string | null;
              last_seen: string;
            };
            
            this.friendsPresence.set(presence.user_id, {
              userId: presence.user_id,
              status: presence.status,
              currentRoomId: presence.current_room_id,
              lastSeen: presence.last_seen,
            });
            
            this.notifyListeners();
          } else if (payload.eventType === 'DELETE') {
            const oldPresence = payload.old as { user_id: string };
            this.friendsPresence.delete(oldPresence.user_id);
            this.notifyListeners();
          }
        }
      )
      .subscribe();
  }

  /**
   * Load friends' current presence
   */
  private async loadFriendsPresence(friendIds: string[]): Promise<void> {
    if (!supabase || friendIds.length === 0) return;

    try {
      const { data: presences } = await supabase
        .from('presence')
        .select('*')
        .in('user_id', friendIds);

      if (presences) {
        presences.forEach((p) => {
          this.friendsPresence.set(p.user_id, {
            userId: p.user_id,
            status: p.status,
            currentRoomId: p.current_room_id,
            lastSeen: p.last_seen,
          });
        });
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Error loading friends presence:', error);
    }
  }

  /**
   * Subscribe to presence changes
   */
  onPresenceChange(callback: PresenceChangeCallback): () => void {
    this.listeners.add(callback);
    
    // Immediately call with current state
    callback(new Map(this.friendsPresence));

    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of presence changes
   */
  private notifyListeners(): void {
    const presenceSnapshot = new Map(this.friendsPresence);
    this.listeners.forEach((callback) => {
      try {
        callback(presenceSnapshot);
      } catch (error) {
        console.error('Error in presence listener:', error);
      }
    });
  }

  /**
   * Get online friends
   */
  getOnlineFriends(): UserPresence[] {
    return Array.from(this.friendsPresence.values()).filter(
      (p) => p.status === 'online' || p.status === 'in_game'
    );
  }

  /**
   * Get presence for a specific user
   */
  getFriendPresence(userId: string): UserPresence | null {
    return this.friendsPresence.get(userId) || null;
  }

  /**
   * Start heartbeat to keep presence updated
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(async () => {
      if (this.currentStatus !== 'offline') {
        await this.updatePresence(this.currentStatus);
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Reset idle timer
   */
  private resetIdleTimer(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }

    this.idleTimeout = setTimeout(async () => {
      if (this.currentStatus === 'online') {
        await this.setAway();
      }
    }, IDLE_TIMEOUT_MS);
  }

  /**
   * Handle user activity (debounced to prevent excessive DB calls)
   */
  private handleActivity = (): void => {
    const now = Date.now();
    this._lastActivityTime = now;
    this.resetIdleTimer();

    // Debounce DB calls - only update if away or if enough time has passed
    if (this.currentStatus === 'away') {
      // Only make the call if we haven't made one recently
      if (now - this._lastActivityDbCall > ACTIVITY_DEBOUNCE_MS) {
        this._lastActivityDbCall = now;
        this.setOnline(); // Fire and forget, no await
      }
    }
  };

  /**
   * Handle visibility change
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.setAway();
    } else {
      this.setOnline();
    }
  };

  /**
   * Handle page unload
   */
  private handleBeforeUnload = (): void => {
    const userId = this.getCurrentUserId();
    if (userId && supabase) {
      this.setOffline();
    }
  };

  /**
   * Set up activity listeners (simplified - only track visibility changes)
   */
  private setupActivityListeners(): void {
    if (typeof window === 'undefined') return;

    // Only listen for meaningful events, not every mouse move
    window.addEventListener('focus', this.handleActivity);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    if (typeof window === 'undefined') return;

    window.removeEventListener('focus', this.handleActivity);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }
}

// Export singleton instance
export const presenceService = new PresenceService();
